import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Search } from "@mui/icons-material";
import axios from "../../../api/axios";
import {
  EXTERNAL_SERVICE_AREA_DEFAULT_RADIUS_KM,
  EXTERNAL_SERVICE_AREA_MAX_RADIUS_KM,
  EXTERNAL_SERVICE_AREA_MIN_RADIUS_KM,
  EXTERNAL_SERVICES_CITY_SEARCH_ENDPOINT,
} from "../../../consts";
import { ExternalServiceArea, PlaceSearchResult } from "../../../types/externalServices";
import { ExternalServiceAreasMap, getAreaColor } from "./externalServiceAreasMap.component";

// The slider covers the radii that are usual for a service area; a larger one
// (up to the maximum) is typed into the field next to it.
const SLIDER_MAX_RADIUS_KM = 100;
const SLIDER_MARKS = [10, 25, 50, 75, 100].map((value) => ({ value, label: `${value}` }));
const MIN_QUERY_LENGTH = 2;

export const areaPlaceKey = (area: Pick<ExternalServiceArea, "city" | "county" | "region">) =>
  `${area.city}|${area.county || ""}|${area.region || ""}`;

/** "powiat wołomiński, województwo mazowieckie" - what tells same-named towns apart. */
export const formatAreaPlace = (area: Pick<ExternalServiceArea, "county" | "region">) =>
  [area.county, area.region].filter(Boolean).join(", ");

const withoutKey = (record: Record<string, string>, key: string) => {
  const next = { ...record };
  delete next[key];
  return next;
};

const clampRadius = (value: number) =>
  Math.min(EXTERNAL_SERVICE_AREA_MAX_RADIUS_KM, Math.max(EXTERNAL_SERVICE_AREA_MIN_RADIUS_KM, Math.round(value)));

export type AreaErrors = Array<Partial<Record<keyof ExternalServiceArea, string>> | null | undefined>;

interface ExternalServiceAreasFieldProps {
  value: ExternalServiceArea[];
  onChange: (areas: ExternalServiceArea[]) => void;
  errors?: AreaErrors;
}

/**
 * Picks the towns an external service works in and how far around each.
 *
 * Towns come from the backend's city search (OpenStreetMap Nominatim), run on
 * an explicit search rather than as you type - Nominatim's usage policy rules
 * out autocomplete.
 */
export default function ExternalServiceAreasField({ value, onChange, errors }: ExternalServiceAreasFieldProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<PlaceSearchResult[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Typed radius text per area, so the field can be cleared mid-edit without
  // the stored radius jumping to the minimum.
  const [radiusDrafts, setRadiusDrafts] = useState<Record<string, string>>({});

  const addedKeys = new Set(value.map(areaPlaceKey));
  const trimmedQuery = query.trim();

  const runSearch = async () => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH || searching) return;
    setSearching(true);
    setSearchError(null);
    try {
      const { data } = await axios.get<{ results: PlaceSearchResult[] }>(EXTERNAL_SERVICES_CITY_SEARCH_ENDPOINT, {
        params: { q: trimmedQuery },
      });
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (error: any) {
      setResults(null);
      setSearchError(
        error?.response?.status === 429
          ? t("Too many searches. Wait a moment and try again.")
          : t("The city search is unavailable right now. Try again shortly.")
      );
    } finally {
      setSearching(false);
    }
  };

  const addPlace = (place: PlaceSearchResult) => {
    if (addedKeys.has(areaPlaceKey(place))) return;
    onChange([
      ...value,
      {
        city: place.city,
        county: place.county,
        region: place.region,
        latitude: place.latitude,
        longitude: place.longitude,
        radius_km: EXTERNAL_SERVICE_AREA_DEFAULT_RADIUS_KM,
      },
    ]);
    setResults(null);
    setQuery("");
  };

  const setRadius = (index: number, radius: number) => {
    onChange(value.map((area, i) => (i === index ? { ...area, radius_km: clampRadius(radius) } : area)));
  };

  const removeArea = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            size="small"
            label={t("Search for a town in Poland")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void runSearch();
              }
            }}
            helperText={t("Type a town and press Enter or Search.")}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={() => void runSearch()}
            disabled={trimmedQuery.length < MIN_QUERY_LENGTH || searching}
            sx={{ minHeight: 40, flexShrink: 0 }}
          >
            {searching ? t("Searching...") : t("Search")}
          </Button>
        </Box>

        {searchError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {searchError}
          </Alert>
        )}

        {results !== null && (
          <Paper variant="outlined" sx={{ mt: 1, maxHeight: 260, overflowY: "auto" }}>
            {results.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                {t("No town found. Check the spelling or try a nearby town.")}
              </Typography>
            ) : (
              <List dense disablePadding>
                {results.map((place) => {
                  const added = addedKeys.has(areaPlaceKey(place));
                  return (
                    <ListItemButton
                      key={`${areaPlaceKey(place)}|${place.latitude}|${place.longitude}`}
                      onClick={() => addPlace(place)}
                      disabled={added}
                    >
                      <ListItemText
                        primary={place.city}
                        secondary={formatAreaPlace(place) || place.display_name}
                      />
                      {added ? (
                        <Typography variant="caption" color="text.secondary">
                          {t("Added")}
                        </Typography>
                      ) : (
                        <Add fontSize="small" color="primary" />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        )}
      </Box>

      {value.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {value.map((area, index) => {
            const key = areaPlaceKey(area);
            const radiusError = errors?.[index]?.radius_km;
            const draft = radiusDrafts[key];
            return (
              <Box
                key={key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr auto", sm: "minmax(160px, 1.2fr) minmax(160px, 2fr) 104px auto" },
                  alignItems: "center",
                  columnGap: 2,
                  rowGap: 0.5,
                  p: 1.5,
                  border: 1,
                  borderColor: radiusError ? "error.main" : "divider",
                  borderLeft: `4px solid ${getAreaColor(index)}`,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {area.city}
                  </Typography>
                  {formatAreaPlace(area) && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {formatAreaPlace(area)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, gridRow: { xs: 2, sm: "auto" }, px: 1 }}>
                  <Slider
                    size="small"
                    value={Math.min(area.radius_km, SLIDER_MAX_RADIUS_KM)}
                    min={EXTERNAL_SERVICE_AREA_MIN_RADIUS_KM}
                    max={SLIDER_MAX_RADIUS_KM}
                    marks={SLIDER_MARKS}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(radius) => `${radius} km`}
                    onChange={(_, radius) => {
                      setRadiusDrafts((prev) => withoutKey(prev, key));
                      setRadius(index, radius as number);
                    }}
                    aria-label={t("Radius (km)")}
                    sx={{ color: getAreaColor(index) }}
                  />
                </Box>
                <TextField
                  size="small"
                  type="number"
                  label={t("Radius")}
                  value={draft ?? String(area.radius_km)}
                  onChange={(e) => {
                    const text = e.target.value;
                    setRadiusDrafts((prev) => ({ ...prev, [key]: text }));
                    const parsed = Number(text);
                    if (text !== "" && Number.isFinite(parsed)) setRadius(index, parsed);
                  }}
                  onBlur={() => setRadiusDrafts((prev) => withoutKey(prev, key))}
                  error={Boolean(radiusError)}
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                  inputProps={{
                    min: EXTERNAL_SERVICE_AREA_MIN_RADIUS_KM,
                    max: EXTERNAL_SERVICE_AREA_MAX_RADIUS_KM,
                    step: 1,
                  }}
                  sx={{ gridColumn: { xs: "1 / 2", sm: "auto" }, gridRow: { xs: 3, sm: "auto" } }}
                />
                <Tooltip title={t("Remove area")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeArea(index)}
                    aria-label={t("Remove area")}
                    sx={{ gridColumn: { xs: 2, sm: "auto" }, gridRow: { xs: 1, sm: "auto" } }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
                {radiusError && (
                  <Typography variant="caption" color="error" sx={{ gridColumn: "1 / -1" }}>
                    {radiusError}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <ExternalServiceAreasMap areas={value} height={280} />
    </Box>
  );
}
